import classNames from "classnames";
import { RefObject, forwardRef, useEffect, useLayoutEffect, useRef } from "react";

import { KeyCodeEnum } from "constants/keyCode";

export interface ICannedResponsesDropdownProps extends React.HTMLAttributes<HTMLDivElement> {
  onClose: () => void;
  className?: string;
  textareaWithCannedResponsesRef: React.RefObject<HTMLDivElement>;
}

export const CannedResponsesDropdown = forwardRef<HTMLDivElement, ICannedResponsesDropdownProps>(
  ({ className, onClose, children, textareaWithCannedResponsesRef, ...restProps }, ref) => {
    const ownDropdownRef = useRef<HTMLDivElement>(null);
    const dropdownRef = (ref as RefObject<HTMLDivElement>) || ownDropdownRef;

    const handleClickOutside = (e: MouseEvent): void => {
      e.stopPropagation();

      if (dropdownRef.current && dropdownRef.current.contains(e.target as Node)) {
        return;
      }

      if (
        (textareaWithCannedResponsesRef.current && textareaWithCannedResponsesRef.current.contains(e.target as Node)) ||
        textareaWithCannedResponsesRef.current?.isSameNode(e.target as Node)
      ) {
        return;
      }

      onClose();
    };

    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.keyCode === KeyCodeEnum.Esc) {
        onClose();
      }
    };

    useEffect(() => {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown);

      return (): void => {
        document.removeEventListener("mousedown", handleClickOutside);
        document.removeEventListener("keydown", handleKeyDown);
      };
    });

    useLayoutEffect(() => {
      const div = dropdownRef && dropdownRef.current;
      if (div) {
        const boundingRect = div.getBoundingClientRect();
        const fixedOffset = div.offsetTop - boundingRect.height;
        div.style.top = `${fixedOffset}px`;
      }

      return (): void => {
        if (div) {
          div.style.top = "";
        }
      };
    }, [children, dropdownRef]);

    return (
      <div {...restProps} data-test="canned-responses-dropdown" ref={dropdownRef} className={classNames(className)}>
        {children}
      </div>
    );
  }
);

CannedResponsesDropdown.displayName = "CannedResponsesDropdown";
